import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SMART_ROUTES } from '../../constants';
import { SmartGalleryChromeMenu } from './SmartHeaderMenu';
import { AppReveal } from './ui';
import { useSessionStore } from './store/useSessionStore';
import { useThemeModeStore } from './store/useThemeModeStore';

interface FundingPageShellProps {
  /** Unused in gallery (home always routes to SMART_ROUTES.HOME). */
  linkId?: string | null;
  children: ReactNode;
}

/**
 * Mobile-first page shell shared by the cash-deposit (InComm) screens. Mirrors
 * the wizard / home chrome — same `max-w-md` column, brand-as-home header and
 * overflow menu — so the funding flow feels native to the smart widget.
 */
export const FundingPageShell = ({ children }: FundingPageShellProps) => {
  const { t } = useTranslation('smart');
  const partner = useSessionStore((s) => s.session?.partner);
  const brandName =
    partner?.headerName ?? partner?.name ?? t('home.brand', 'Lola Send');
  const mode = useThemeModeStore((s) => s.mode);
  const setMode = useThemeModeStore((s) => s.setMode);

  useEffect(() => {
    setMode(mode);
  }, [mode, setMode]);

  return (
    <div className="smart-root">
      <AppReveal>
        <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col px-5 pb-10 pt-8">
          <header className="mb-6 flex items-center justify-between">
            <Link
              to={SMART_ROUTES.HOME}
              aria-label={t('common.home', 'Home')}
              className="text-accent text-lg font-bold uppercase tracking-tight transition-opacity hover:opacity-80"
            >
              {brandName}
            </Link>
            <SmartGalleryChromeMenu />
          </header>

          {children}
        </main>
      </AppReveal>
    </div>
  );
};
